import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { environment } from '@environments/environment'
import { REFRESH_TOKEN_KEY } from '@utils/storage.keys'
import { catchError, from, Observable, of } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  private readonly http: HttpClient

  constructor(http: HttpClient) {
    this.http = http

    // proactively update existing worker on service creation. we don't
    // await this since it's noncritical and should not block app startup.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistration('/assets/sw-push.js')
        .then((reg) => reg?.update())
        .catch(() => {
          // ignore failures updating; not critical
        })
    }
  }

  private normalizeLanguage(lang?: string | null): string {
    const supported = ['en', 'es', 'sv', 'fr', 'it']
    const primary = String(lang || '').toLowerCase().split('-')[0]
    return supported.includes(primary) ? primary : 'en'
  }

  private async postLanguageToServiceWorker(lang: string): Promise<void> {
    if (!('serviceWorker' in navigator)) return

    const registration =
      (await navigator.serviceWorker.getRegistration('/assets/sw-push.js')) ||
      (await navigator.serviceWorker.getRegistration())

    if (!registration) return

    const target =
      registration.active ||
      registration.waiting ||
      registration.installing ||
      navigator.serviceWorker.controller

    target?.postMessage({
      type: 'SET_APP_LANGUAGE',
      language: this.normalizeLanguage(lang),
    })
  }

  private safeStorageGet(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }


  /**
   * Parse the iOS version from the user agent string (e.g. 16.4).
   * Returns `null` if not running on iOS or version could not be determined.
   */
  private static getIOSVersion(): number | null {
    const match = navigator.userAgent.match(/OS (\d+)_?(\d+)?_?(\d+)?/i)
    if (!match) {
      return null
    }
    const major = parseInt(match[1], 10)
    const minor = match[2] ? parseInt(match[2], 10) : 0
    return parseFloat(`${major}.${minor}`)
  }

  /**
   * Historically the app disabled web push on all iOS devices because the
   * platform did not support it.  Starting with Safari 16.4 (iOS 16.4)
   * Apple added WebPush support for installed PWAs.  The logic used by the
   * UI only needs to know whether the browser *can* request a subscription.
   *
   * We treat only versions older than 16.4 as unsupported; newer versions
   * fall back to the normal feature detection below.
   */
  private static isUnsupportedOldIOS(): boolean {
    const v = PushNotificationsService.getIOSVersion()
    return v !== null && v < 16.4
  }

  private static isIOSDevice(): boolean {
    const ua = navigator.userAgent || ''
    const iosByUA = /iPad|iPhone|iPod/i.test(ua)
    const ipadOSDesktopUA =
      navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
    return iosByUA || ipadOSDesktopUA
  }

  private static isStandaloneDisplayMode(): boolean {
    const nav = navigator as Navigator & { standalone?: boolean }
    return (
      nav.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches
    )
  }

  private static isPushContextSupportedOnIOS(): boolean {
    // On iOS/iPadOS, web push is only available in installed web apps (standalone).
    if (!PushNotificationsService.isIOSDevice()) {
      return true
    }
    return PushNotificationsService.isStandaloneDisplayMode()
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  public requestSubscription(): Observable<NotificationPermission> {
    // wrap promise-based logic in an observable for backwards compatibility
    return from((async () => {
      // early-out for *unsupported* iOS versions only; newer releases
      // (16.4+) actually support web push when running as an installed PWA.
      if (PushNotificationsService.isUnsupportedOldIOS()) {
        return 'default' as NotificationPermission
      }
      if (!PushNotificationsService.isPushContextSupportedOnIOS()) {
        return 'default' as NotificationPermission
      }
      if (Notification.permission === 'denied') {
        return Notification.permission as NotificationPermission
      }

      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('Push not supported in this browser')
        return Notification.permission as NotificationPermission
      }

      // register with no cache and immediately check for updates
      const registration = await navigator.serviceWorker.register(
        '/assets/sw-push.js',
        { updateViaCache: 'none' },
      )
      await registration.update()

      await this.postLanguageToServiceWorker(
        this.safeStorageGet('lang') || navigator.language || 'en',
      )
      const publicKey = await this.http.get(`${environment.apiEndPoint}/notifications/vapidPublicKey`, { responseType: 'text' }).toPromise()
      if (!publicKey || typeof publicKey !== 'string') {
        throw new Error('VAPID public key not available from server')
      }
      const keyArray = this.urlBase64ToUint8Array(publicKey)
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // BufferSource accepts ArrayBufferView or ArrayBuffer; cast to satisfy strict typing
        applicationServerKey: keyArray as unknown as BufferSource,
      })

      // send to backend
      await this.http.post(`${environment.apiEndPoint}/notifications/web/subscribe`, {
        subscription,
        refreshToken: this.safeStorageGet(REFRESH_TOKEN_KEY),
      }).toPromise()

      return Notification.permission as NotificationPermission
    })()).pipe(
      catchError((err) => {
        console.warn('Push subscription failed; continuing without push', err)
        return of((Notification.permission ?? 'default') as NotificationPermission)
      }),
    )
  }

  public syncServiceWorkerLanguage(lang?: string): Observable<void> {
    // try to refresh the service worker before sending it a message so that
    // clients on the next page load will have the latest code.
    const promise = (async () => {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration('/assets/sw-push.js')
        if (reg) {
          try {
            await reg.update()
          } catch {
            // swallow any error; we’ll try again later
          }
        }
      }
      return this.postLanguageToServiceWorker(
        lang || this.safeStorageGet('lang') || navigator.language || 'en',
      )
    })()

    return from(promise).pipe(
      catchError((err) => {
        console.warn('Unable to sync language to service worker', err)
        return of(void 0)
      }),
    )
  }

  public unsubscribe(): Observable<boolean> {
    return from((async () => {
      if (PushNotificationsService.isUnsupportedOldIOS()) return false
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false

      const registration = await navigator.serviceWorker.getRegistration('/assets/sw-push.js')
      if (!registration) return false
      const sub = await registration.pushManager.getSubscription()
      if (!sub) {
        await this.http.post(`${environment.apiEndPoint}/notifications/web/unsubscribe`, {
          refreshToken: this.safeStorageGet(REFRESH_TOKEN_KEY),
        }).toPromise()
        return true
      }

      const unsubscribed = await sub.unsubscribe()
      if (unsubscribed) {
        await this.http.post(`${environment.apiEndPoint}/notifications/web/unsubscribe`, {
          endpoint: sub.endpoint,
          refreshToken: this.safeStorageGet(REFRESH_TOKEN_KEY),
        }).toPromise().catch(err => console.warn('backend unsubscribe failed', err))
      }
      return unsubscribed
    })())
  }

  public isPushSupported(): boolean {
    // only block old versions; newer iOS may properly support push
    if (PushNotificationsService.isUnsupportedOldIOS()) return false
    if (!PushNotificationsService.isPushContextSupportedOnIOS()) return false
    if (typeof Notification === 'undefined') return false
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false
    return true
  }

  // Helper to initialise the prompt permission value
  initNotificationAsk(): Observable<NotificationPermission> {
    if (PushNotificationsService.isUnsupportedOldIOS()) {
      return of()
    }
    if (!PushNotificationsService.isPushContextSupportedOnIOS()) {
      return of('default')
    }
    return from(Promise.resolve(Notification.permission as NotificationPermission))
  }
}
